import { all, call, put, take, select } from 'redux-saga/effects';

import {
  getPatientRoundHistory,
  getEmployeeRoundHistory,
  postRoundSuccess,
  postRoundFailure,
  getRoundSuccess,
  getRoundFailure,
  clearRoundState,
  postNewTask,
  clearSharingQ,
  getTasksByAccountNum,
  getTasksByEmployeeId,
  getTemplateByIdSuccess,
  getTemplateByIdFailure,
  clearTemplateById,
  getPatientRoundHistorySuccess,
  setToastOptions,
  slideIn
} from '../actions';

import { getData, postData } from '../../services/API';
import {
  templatePublishedByLocationAndTypeUrl,
  getRoundUrl,
  templateByIdUrl,
  templateMasterUrl,
  postRoundUrl
} from '../../services/API/endpoints';

import supportRoundingTypes from '../../constants/supportRoundingTypes.json';

/**
 * Handle post-round operations for validation types
 * Fetch updated subject list and round history based on validation type
 *
 * @param {Object} updatedRound - The round data that was posted
 * @param {string} type - The validation type (validation, aidetValidation, nlrValidation, bssrValidation)
 */
function* handleValidationRoundPost(updatedRound, type) {
  yield put(
    getEmployeeRoundHistory({
      employeeId: updatedRound.employeeId,
      roundingTypes: [type]
    })
  );
}

// Round sagas
export function* postRoundSaga({ payload }) {
  const { type } = payload;

  // Select round data from the store
  const { currentRound, isRoundStarted, pendingFeedbacks, pendingIssues } =
    yield select(state => state.RoundReducer);
  const { facilityId } = yield select(
    state => state.AuthorizedUser.authorizedUser
  );

  // If there is no round data, return
  if (!Object.keys(currentRound).length > 0) return;

  const url = `${postRoundUrl}`;
  const multiShare = pendingIssues.length + pendingFeedbacks.length > 1;

  const updatedRound = { ...currentRound };
  try {
    // if Issues attached to a round post it to /task
    if (
      (pendingIssues.length > 0 || pendingFeedbacks.length > 0) &&
      isRoundStarted
    ) {
      yield all([
        ...pendingFeedbacks.map(fb =>
          put(
            postNewTask({
              ...fb,
              roundId: updatedRound.roundId,
              multiShare
            })
          )
        ),
        ...pendingIssues.map(issue =>
          put(
            postNewTask({
              ...issue,
              roundId: updatedRound.roundId,
              multiShare
            })
          )
        )
      ]);

      if (multiShare) {
        // This is to make sure all tasks are posted before clearing the queue and showing the toast
        let completeTaskCount = 0;
        const totalTasks = pendingIssues.length + pendingFeedbacks.length;
        while (completeTaskCount < totalTasks) {
          yield take('POST_NEW_TASK_SUCCESS');
          completeTaskCount += 1;
        }
        yield put(clearSharingQ());
      }
      yield put(
        setToastOptions({
          msg: 'All round tasks posted!',
          feedback: 'success',
          duration: 1000,
          speed: 500
        })
      );
      yield put(slideIn());
      // add the taskIds to the round
      updatedRound.taskIds = [
        ...updatedRound.taskIds,
        ...pendingIssues.map(issue => issue.taskId),
        ...pendingFeedbacks.map(fb => fb.taskId)
      ];
    }
    // Call the POST /round endpoint with the round payload
    const { status } = yield call(postData, url, updatedRound);
    // If successful remove loading flags from the store
    if (status === 200) {
      const { roundingType } = updatedRound;

      // Update store with new round info
      if (type === 'patient') {
        yield put(
          getPatientRoundHistory({
            facilityId,
            patientId: updatedRound.patientId || updatedRound.accountNum
          })
        );
        // if issues attached
        if (
          (pendingIssues.length > 0 || pendingFeedbacks.length > 0) &&
          isRoundStarted &&
          updatedRound.accountNum
        ) {
          yield put(
            getTasksByAccountNum({
              accountNumber: updatedRound.accountNum,
              facilityMnemonic: facilityId
            })
          );
        }
      } else if (type === 'employee') {
        yield put(
          getEmployeeRoundHistory({
            employeeId: updatedRound.employeeId,
            roundingTypes: ['employee', 'cned', 'csrn']
          })
        );
        if (
          (pendingIssues.length > 0 || pendingFeedbacks.length > 0) &&
          isRoundStarted
        ) {
          yield put(
            getTasksByEmployeeId({
              employeeId: updatedRound.employeeId,
              roundingTypes: ['employee', 'cned', 'csrn']
            })
          );
        }
      } else if (supportRoundingTypes.validation.includes(type)) {
        yield* handleValidationRoundPost(updatedRound, type);
      } else {
        yield put(
          getEmployeeRoundHistory({
            employeeId: updatedRound.employeeId,
            roundingTypes: ['csrn', 'cned']
          })
        );
        if (
          (roundingType === 'cned' || roundingType === 'csrn') &&
          isRoundStarted &&
          (pendingIssues.length > 0 || pendingFeedbacks.length > 0)
        ) {
          yield put(
            getTasksByEmployeeId({
              employeeId: updatedRound.employeeId,
              roundingTypes: ['employee', 'cned', 'csrn']
            })
          );
        }
      }
      yield put(postRoundSuccess());
    } else {
      yield put(
        setToastOptions({
          msg: 'Round failed to Post!',
          feedback: 'error',
          duration: null,
          speed: 500,
          question: true
        })
      );
      yield put(slideIn());
      yield put(postRoundFailure());
    }
  } catch (error) {
    console.log(error);
    yield put(
      setToastOptions({
        msg: 'Round failed to Post!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    yield put(slideIn());
    yield put(postRoundFailure());
  }
}

export function* getRoundSaga({ payload }) {
  const { roundId, fromHistoryDrawer, type, accountNum, unitId } = payload;
  const facilityId = yield select(
    state => state.AuthorizedUser.authorizedUser.facilityId
  );
  const division = yield select(
    state =>
      state.ConfigReducer.userAccessibleFacilities.filter(
        facility => facility.facilityId === facilityId
      )[0].division
  );

  yield put(clearRoundState());
  yield put(clearTemplateById());
  const url = `${getRoundUrl}?roundId=${roundId}`;

  try {
    const response = yield call(getData, url);

    if (response.status === 200) {
      // TODO: create conditional where if NO templateConfigId then look up template
      if (
        accountNum &&
        accountNum.substring(0, 2) === 'UL' &&
        !fromHistoryDrawer
      ) {
        yield put(getPatientRoundHistorySuccess(response.data));
        return yield put(getRoundSuccess({}));
      }
      const templatesUrl =
        type === 'patient'
          ? `${templatePublishedByLocationAndTypeUrl}?roundingType=${type}&division=${division}&facility=${facilityId}&unit=${unitId}&condensed=true&archived=false`
          : `${templateMasterUrl}?condensed=true&archived=false`;

      const { data: masterTemplateData } = yield call(getData, templatesUrl);

      const foundMasterTemplate = masterTemplateData.find(
        template => template.templateId === response.data[0].templateId
      );

      if (!foundMasterTemplate) {
        const secondaryUrl = `${templateByIdUrl}?templateId=${response.data[0].templateId}`;

        try {
          const { data, status } = yield call(getData, secondaryUrl);
          if (status === 200) {
            yield put(getTemplateByIdSuccess(data));
          } else {
            yield put(getTemplateByIdFailure());
          }
        } catch (err) {
          yield put(getTemplateByIdFailure());
        }
        return yield put(getRoundSuccess(response.data[0]));
      }
      return yield put(getRoundSuccess(response.data[0]));
    }
    return yield put(getRoundFailure());
  } catch (error) {
    console.log(error);
    return yield put(getRoundFailure());
  }
}
